import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import { FloatingAIChat } from "@/components/FloatingAIChat";
import { CookieConsent } from "@/components/CookieConsent";
import { useAuth } from "@/hooks/useAuth";
import { lazy, Suspense, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supportedLanguages } from "@/i18n";

// Eagerly load Landing for fastest LCP
import Landing from "./pages/Landing";

// Lazy-load all other pages for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Teams = lazy(() => import("./pages/Teams"));
const TeamDetail = lazy(() => import("./pages/TeamDetail"));
const KPIs = lazy(() => import("./pages/KPIs"));
const KPIDetail = lazy(() => import("./pages/KPIDetail"));
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const Press = lazy(() => import("./pages/Press"));
const Affiliates = lazy(() => import("./pages/Affiliates"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Updates = lazy(() => import("./pages/Updates"));
const Profile = lazy(() => import("./pages/Profile"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const Billing = lazy(() => import("./pages/Billing"));
const Sprints = lazy(() => import("./pages/Sprints"));
const CompanySettings = lazy(() => import("./pages/CompanySettings"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SmallStartups = lazy(() => import("./pages/usecases/SmallStartups"));
const SmallMediumCompanies = lazy(() => import("./pages/usecases/SmallMediumCompanies"));
const Enterprise = lazy(() => import("./pages/usecases/Enterprise"));
const ITTeams = lazy(() => import("./pages/usecases/ITTeams"));
const Operations = lazy(() => import("./pages/usecases/Operations"));
const Sales = lazy(() => import("./pages/usecases/Sales"));
const ManagementBoard = lazy(() => import("./pages/usecases/ManagementBoard"));
const ProjectManagement = lazy(() => import("./pages/usecases/ProjectManagement"));
const PublicInstitutions = lazy(() => import("./pages/usecases/PublicInstitutions"));
const Nonprofits = lazy(() => import("./pages/usecases/Nonprofits"));
const HRTeams = lazy(() => import("./pages/usecases/HRTeams"));
const Manufacturing = lazy(() => import("./pages/usecases/Manufacturing"));
const Consulting = lazy(() => import("./pages/Consulting"));
const BrandGuidelines = lazy(() => import("./pages/BrandGuidelines"));
const SuperAdminGuard = lazy(() => import("./pages/superadmin/SuperAdminGuard").then(m => ({ default: m.SuperAdminGuard })));
const SuperAdminDashboard = lazy(() => import("./pages/superadmin/SuperAdminDashboard"));
const SuperAdminCompanies = lazy(() => import("./pages/superadmin/SuperAdminCompanies"));
const SuperAdminCompanyDetail = lazy(() => import("./pages/superadmin/SuperAdminCompanyDetail"));
const SuperAdminAnalytics = lazy(() => import("./pages/superadmin/SuperAdminAnalytics"));
const SuperAdminBilling = lazy(() => import("./pages/superadmin/SuperAdminBilling"));
const SuperAdminUsers = lazy(() => import("./pages/superadmin/SuperAdminUsers"));
const SuperAdminAuditLog = lazy(() => import("./pages/superadmin/SuperAdminAuditLog"));
const SuperAdminSupport = lazy(() => import("./pages/superadmin/SuperAdminSupport"));
const SuperAdminSettings = lazy(() => import("./pages/superadmin/SuperAdminSettings"));
const SuperAdminProfile = lazy(() => import("./pages/superadmin/SuperAdminProfile"));

const queryClient = new QueryClient();

function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
}

// Syncs the i18n language with the URL :lang param
function LanguageSync() {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  
  useEffect(() => {
    if (lang && supportedLanguages.includes(lang as any) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);
  
  return null;
}

// Component to conditionally show floating AI chat
function FloatingAIChatWrapper() {
  const location = useLocation();
  const { user } = useAuth();
  
  // Match both prefixed and unprefixed public routes
  const pathWithoutLang = location.pathname.replace(/^\/(en|et)/, '') || '/';
  const publicRoutes = ['/', '/auth', '/reset-password', '/pricing', '/about', '/blog', '/invite', '/use-cases', '/help-center', '/faq', '/contact', '/press', '/affiliates', '/terms', '/privacy', '/cookie-policy', '/updates', '/consulting'];
  const isPublicRoute = publicRoutes.some(route => 
    pathWithoutLang === route || pathWithoutLang.startsWith('/blog/') || pathWithoutLang.startsWith('/invite/') || pathWithoutLang.startsWith('/use-cases/')
  );
  
  if (isPublicRoute || !user) return null;
  
  return <FloatingAIChat />;
}

// Redirect bare paths to /en/ prefixed paths
function LangRedirect() {
  const location = useLocation();
  const browserLang = navigator.language?.substring(0, 2);
  const defaultLang = supportedLanguages.includes(browserLang as any) ? browserLang : 'en';
  return <Navigate to={`/${defaultLang}${location.pathname}${location.search}${location.hash}`} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Language-prefixed routes */}
      <Route path="/:lang/*" element={<LocalizedRoutes />} />
      
      {/* Redirect bare paths to language-prefixed */}
      <Route path="/" element={<LangRedirect />} />
      <Route path="/*" element={<LangRedirect />} />
    </Routes>
  );
}

function LocalizedRoutes() {
  const { lang } = useParams<{ lang: string }>();
  
  // If the "lang" param isn't a supported language, treat as a bare path redirect
  if (!supportedLanguages.includes(lang as any)) {
    return <LangRedirect />;
  }

  return (
    <>
      <LanguageSync />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/help-center" element={<HelpCenter />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/press" element={<Press />} />
        <Route path="/affiliates" element={<Affiliates />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/updates" element={<Updates />} />
        <Route path="/consulting" element={<Consulting />} />
        <Route path="/brand-guidelines" element={<BrandGuidelines />} />
        <Route path="/invite/:token" element={<AcceptInvitation />} />
        
        {/* Use Case routes */}
        <Route path="/use-cases/small-startups" element={<SmallStartups />} />
        <Route path="/use-cases/small-medium-companies" element={<SmallMediumCompanies />} />
        <Route path="/use-cases/enterprise" element={<Enterprise />} />
        <Route path="/use-cases/it-teams" element={<ITTeams />} />
        <Route path="/use-cases/operations" element={<Operations />} />
        <Route path="/use-cases/sales" element={<Sales />} />
        <Route path="/use-cases/management-board" element={<ManagementBoard />} />
        <Route path="/use-cases/project-management" element={<ProjectManagement />} />
        <Route path="/use-cases/public-institutions" element={<PublicInstitutions />} />
        <Route path="/use-cases/nonprofits" element={<Nonprofits />} />
        <Route path="/use-cases/hr-teams" element={<HRTeams />} />
        <Route path="/use-cases/manufacturing" element={<Manufacturing />} />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
        <Route path="/teams/:id" element={<ProtectedRoute><TeamDetail /></ProtectedRoute>} />
        <Route path="/kpis" element={<ProtectedRoute><KPIs /></ProtectedRoute>} />
        <Route path="/kpis/:id" element={<ProtectedRoute><KPIDetail /></ProtectedRoute>} />
        <Route path="/sprints" element={<ProtectedRoute><Sprints /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
        <Route path="/company-settings" element={<ProtectedRoute><CompanySettings /></ProtectedRoute>} />
        
        {/* SuperAdmin routes */}
        <Route path="/superadmin" element={<Suspense fallback={<PageLoader />}><SuperAdminGuard><SuperAdminDashboard /></SuperAdminGuard></Suspense>} />
        <Route path="/superadmin/companies" element={<Suspense fallback={<PageLoader />}><SuperAdminGuard><SuperAdminCompanies /></SuperAdminGuard></Suspense>} />
        <Route path="/superadmin/companies/:id" element={<Suspense fallback={<PageLoader />}><SuperAdminGuard><SuperAdminCompanyDetail /></SuperAdminGuard></Suspense>} />
        <Route path="/superadmin/analytics" element={<Suspense fallback={<PageLoader />}><SuperAdminGuard><SuperAdminAnalytics /></SuperAdminGuard></Suspense>} />
        <Route path="/superadmin/billing" element={<Suspense fallback={<PageLoader />}><SuperAdminGuard><SuperAdminBilling /></SuperAdminGuard></Suspense>} />
        <Route path="/superadmin/users" element={<Suspense fallback={<PageLoader />}><SuperAdminGuard><SuperAdminUsers /></SuperAdminGuard></Suspense>} />
        <Route path="/superadmin/audit-log" element={<Suspense fallback={<PageLoader />}><SuperAdminGuard><SuperAdminAuditLog /></SuperAdminGuard></Suspense>} />
        <Route path="/superadmin/support" element={<Suspense fallback={<PageLoader />}><SuperAdminGuard><SuperAdminSupport /></SuperAdminGuard></Suspense>} />
        <Route path="/superadmin/settings" element={<Suspense fallback={<PageLoader />}><SuperAdminGuard><SuperAdminSettings /></SuperAdminGuard></Suspense>} />
        <Route path="/superadmin/profile" element={<Suspense fallback={<PageLoader />}><SuperAdminGuard><SuperAdminProfile /></SuperAdminGuard></Suspense>} />
        
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <FloatingAIChatWrapper />
        <CookieConsent />
        <Suspense fallback={<PageLoader />}>
          <AppRoutes />
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
